import type { VisibleRange } from '@src/features/absence';
import { useTimeline } from '@src/features/absence';
import { useAuthentication } from '@src/features/auth';
import { useFormContext } from '@src/shared/form';
import { formatIsoDate } from '@src/shared/lib';
import Box from '@src/shared/ui/layout/Box';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import {
  buildHolidayLookup,
  getAbsencesIdsForRange,
  getUserAbsences,
  isHrOfUser,
  userResourceId,
} from '../../../lib';
import {
  applyNotesHrEntitlement,
  seedDraftFields,
  shouldFollowEntitlement,
} from '../draft';
import type { ScheduleFormValues, WithdrawScope } from '../Form';
import TotalDatesOrHours from '../TotalDatesOrHours';
import { DatesRangeField } from './Dates';
import HoursToggle from './HoursToggle';
import NotesField from './NotesField';
import NotesHrField from './NotesHrField';
import TimeRangeField from './Time';
import TypeField from './TypeField';
import UsersField from './User';

type FormFieldsetProps = {
  disabled?: boolean;
  disableUserField?: boolean;
  /** Non-null while withdrawing: locks granularity and bounds the range. */
  withdrawScope?: WithdrawScope | null;
};

const SCROLL_LEAD_DAYS = 7;

function isWithinVisibleRange(date: Date, range: VisibleRange) {
  const t = date.getTime();
  return t >= range.start.getTime() && t < range.end.getTime();
}

function areIdsEqual(left: number[], right: number[]) {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((id, index) => id === right[index]);
}

export default function FormFieldset({
  disabled = false,
  disableUserField = false,
  withdrawScope = null,
}: FormFieldsetProps) {
  const form = useFormContext() as UseFormReturn<ScheduleFormValues>;
  const { setValue, watch } = form;
  const { user: currentUser } = useAuthentication();
  const {
    absences,
    selectedSlot,
    setSelectedSlot,
    selectedSlotData,
    calendar,
    isDayView,
    setSelectedUserId,
    expandAndScrollToResource,
    holidays,
    users,
    visibleRange,
  } = useTimeline();
  const holidayLookup = useMemo(() => buildHolidayLookup(holidays), [holidays]);
  const hours = withdrawScope ? withdrawScope.hourly : isDayView;
  const withdrawing = !!withdrawScope;
  const userId = watch('userId');
  const typeId = watch('typeId');
  const selectedUserBranchId = useMemo(
    () => users.find((user) => user.id === userId)?.branch?.id ?? null,
    [userId, users],
  );
  const isHrOf = useCallback(
    (targetId: number | null) => isHrOfUser(targetId, users, currentUser),
    [currentUser, users],
  );
  // For the *form's* employee: what the field may show while it is being
  // edited.
  const isHrOfTarget = isHrOf(userId);
  const [dateFrom, dateTo] = watch(['dateFrom', 'dateTo']);
  const [startTime, endTime] = watch(['startTime', 'endTime']);

  useEffect(() => {
    setSelectedSlot((slot) => {
      if (!slot) return null;
      let nextStartTime: string | null = hours ? startTime || null : null;
      let nextEndTime: string | null = hours ? endTime || null : null;

      if (hours && !nextStartTime && !nextEndTime) {
        const editedId = slot.editedAbsenceId ?? slot.editedAbsenceIds?.[0];
        const editedAbsence = editedId
          ? absences.find((a) => a.id === editedId)
          : null;

        if (
          editedAbsence &&
          !editedAbsence.allDay &&
          editedAbsence.startTime != null &&
          editedAbsence.endTime != null
        ) {
          nextStartTime = editedAbsence.startTime;
          nextEndTime = editedAbsence.endTime;
        } else {
          const slotDate = dateFrom ? formatIsoDate(dateFrom) : slot.start;
          const userAbsences = getUserAbsences(slot.userId, absences);
          const firstHourly = userAbsences
            .filter(
              (a) =>
                a.dateStr === slotDate &&
                !a.allDay &&
                a.startTime != null &&
                a.endTime != null,
            )
            .sort((a, b) =>
              (a.startTime ?? '').localeCompare(b.startTime ?? ''),
            )[0];

          if (firstHourly) {
            nextStartTime = firstHourly.startTime;
            nextEndTime = firstHourly.endTime;
          }
        }
      }
      let selectedIds: number[] = [];
      if (dateFrom && dateTo) {
        selectedIds = getAbsencesIdsForRange(
          dateFrom,
          dateTo,
          getUserAbsences(slot?.userId, absences),
          {
            dayView: hours,
            startTime: nextStartTime,
            endTime: nextEndTime,
            // Withdrawal acts on the edited absences themselves, so they
            // must stay in `selectedIds` for the range to stay highlighted.
            excludeIds: slot.withdrawing
              ? []
              : (slot.editedAbsenceIds ??
                (slot.editedAbsenceId ? [slot.editedAbsenceId] : [])),
          },
        );
      } else if (slot.start && slot.end) {
        selectedIds = getAbsencesIdsForRange(
          new Date(slot.start),
          new Date(slot.end),
          getUserAbsences(slot?.userId, absences),
          {
            dayView: hours,
            startTime: nextStartTime,
            endTime: nextEndTime,
            // Withdrawal acts on the edited absences themselves, so they
            // must stay in `selectedIds` for the range to stay highlighted.
            excludeIds: slot.withdrawing
              ? []
              : (slot.editedAbsenceIds ??
                (slot.editedAbsenceId ? [slot.editedAbsenceId] : [])),
          },
        );
      }
      const changedSlot = {
        userId,
        draftTypeId: typeId,
        resourceId: userResourceId(userId),
        ...(dateFrom ? { start: formatIsoDate(dateFrom) } : {}),
        ...(dateTo ? { end: formatIsoDate(dateTo) } : {}),
        startTime: nextStartTime,
        endTime: nextEndTime,
      };
      const nextSlot = {
        ...slot,
        ...changedSlot,
        selectedIds,
      };

      if (
        slot.userId === nextSlot.userId &&
        slot.draftTypeId === nextSlot.draftTypeId &&
        slot.resourceId === nextSlot.resourceId &&
        slot.start === nextSlot.start &&
        slot.end === nextSlot.end &&
        slot.startTime === nextSlot.startTime &&
        slot.endTime === nextSlot.endTime &&
        slot.editedAbsenceId === nextSlot.editedAbsenceId &&
        areIdsEqual(slot.selectedIds, nextSlot.selectedIds)
      ) {
        return slot;
      }

      return nextSlot;
    });
  }, [
    absences,
    userId,
    typeId,
    dateFrom,
    dateTo,
    endTime,
    hours,
    setSelectedSlot,
    startTime,
  ]);

  const visibleRangeRef = useRef(visibleRange);
  visibleRangeRef.current = visibleRange;

  useEffect(() => {
    if (!dateFrom || !dateTo || hours) return;

    const range = visibleRangeRef.current;
    const fromOutside = !isWithinVisibleRange(dateFrom, range);
    const toOutside = !isWithinVisibleRange(dateTo, range);

    if (!fromOutside && !toOutside) return;

    const anchor = new Date(dateFrom);
    anchor.setDate(anchor.getDate() - SCROLL_LEAD_DAYS);
    calendar.setMonthViewFrom(anchor);
  }, [dateFrom, dateTo, calendar, hours]);

  useEffect(() => {
    setSelectedUserId(userId ?? null);
    if (userId) {
      // Delay scroll to run after selection sync updates
      // the calendar layout.
      const timer = setTimeout(() => {
        expandAndScrollToResource(userId);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [userId, setSelectedUserId, expandAndScrollToResource]);

  // Which draft these values belong to. The fields are mounted across a switch
  // from one absence to another, so "the same draft" is the only thing that
  // distinguishes seeding from overwriting.
  const draftId = selectedSlot?.draftId ?? null;
  const slotStart = selectedSlotData?.start ?? null;
  const slotEnd = selectedSlotData?.end ?? null;
  const slotStartTime = selectedSlotData?.startTime ?? null;
  const slotEndTime = selectedSlotData?.endTime ?? null;
  const slotUserId = selectedSlotData?.userId ?? null;
  const slotTypeId = selectedSlotData?.typeId ?? null;
  const slotTentative = selectedSlotData?.tentative ?? false;
  const slotNotes = selectedSlotData?.notes ?? '';
  const slotNotesHr = selectedSlotData?.notesHr ?? '';

  // The dates and times a *gesture* can move: dragging or resizing on the
  // timeline is the same edit given a different range, and the fields have to
  // follow it. Per field, so moving the range does not re-hydrate a value the
  // user has cleared in a field the gesture said nothing about.
  useEffect(() => {
    if (slotStart) setValue('dateFrom', new Date(slotStart));
  }, [slotStart, setValue]);

  useEffect(() => {
    if (slotEnd) setValue('dateTo', new Date(slotEnd));
  }, [slotEnd, setValue]);

  useEffect(() => {
    setValue('startTime', slotStartTime);
  }, [slotStartTime, setValue]);

  useEffect(() => {
    setValue('endTime', slotEndTime);
  }, [slotEndTime, setValue]);

  useEffect(() => {
    setValue('userId', slotUserId);
  }, [slotUserId, setValue]);

  /**
   * Everything else the draft opened with, seeded once and then the user's.
   *
   * The type, the tentative flag and the notes are read off the records the
   * draft opened on -- and those records are refetched on every
   * absence-changed message, on socket connect and whenever the tab comes back
   * into view. Re-hydrating from them meant somebody else editing this absence
   * silently replaced the type the user had just chosen, or the notes they were
   * halfway through typing. A gesture never moves these, so nothing is lost by
   * seeding them once.
   *
   * Keyed on the draft, and only once its records are in hand: a draft opened
   * from a deep link exists before the days it names have arrived, and there is
   * no earlier value for a later one to overwrite.
   */
  const seededDraft = useRef<number | null>(null);
  /** What this draft put in the HR note, so a later change can tell whether
   * the user has since typed over it. */
  const seededNotesHr = useRef<string | undefined>(undefined);
  const namesRecords = !!(
    selectedSlot?.editedAbsenceId ||
    selectedSlot?.editedAbsenceIds?.length ||
    selectedSlot?.selectedIds.length
  );
  useEffect(() => {
    if (draftId === null || seededDraft.current === draftId) return;
    // A draft that names days but cannot yet say what type they are has not
    // had its records resolved. Seeding `null` now and never again would leave
    // the form blank for the rest of the edit.
    if (namesRecords && slotTypeId === null) return;
    // Whether the HR note may be shown at all is worked out from the employee
    // list, so seeding before it lands would decide that question wrongly and
    // never revisit it.
    if (!users.length) return;
    seededDraft.current = draftId;
    // Entitlement asked of the *draft's* employee, not the form's. On the
    // render a new draft arrives the form still holds the previous one -- the
    // effect that copies the employee across has not run -- so asking the form
    // would decide this against the wrong person and, because the draft is now
    // marked seeded, never revisit it.
    const notesHrForDraft = isHrOf(slotUserId) ? slotNotesHr : undefined;
    seededNotesHr.current = notesHrForDraft;
    seedDraftFields(form, {
      typeId: slotTypeId,
      tentative: slotTentative,
      notes: slotNotes,
      notesHr: notesHrForDraft,
    });
  }, [
    draftId,
    form,
    isHrOf,
    namesRecords,
    slotNotes,
    slotNotesHr,
    slotTentative,
    slotTypeId,
    slotUserId,
    users.length,
  ]);

  /**
   * Entitlement changing under an open draft, which seeding cannot cover.
   *
   * The employee can be changed in the form, and the employee list can land
   * after the draft opened. Either can turn the HR note from the viewer's
   * business into somebody else's or back.
   *
   * Losing it clears the field, because that decides what the payload carries
   * and safety outweighs a value in an input nobody may send. Gaining it seeds
   * from the record only while the field is untouched, so a note being typed is
   * never overwritten.
   */
  const entitledToNotesHr = isHrOfTarget;
  useEffect(() => {
    if (
      !shouldFollowEntitlement({
        seededDraftId: seededDraft.current,
        draftId,
        formUserId: userId ?? null,
        draftUserId: slotUserId,
      })
    ) {
      return;
    }
    applyNotesHrEntitlement(form, {
      entitled: entitledToNotesHr,
      notesHr: slotNotesHr,
      seeded: seededNotesHr.current,
    });
  }, [draftId, entitledToNotesHr, form, slotNotesHr, slotUserId, userId]);

  const onHoursToggle = (hours: boolean) => {
    if (hours !== null) {
      if (hours) {
        if (dateFrom && dateTo && dateFrom.getTime() !== dateTo.getTime()) {
          setValue('dateTo', dateFrom);
        }
        calendar.setDayView(dateFrom || new Date());
      } else {
        const date = dateFrom || new Date();

        calendar.setMonthView(date.getFullYear(), date.getMonth());
      }
    }
  };

  return (
    <Box sx={{ flexGrow: 1, mx: -2.5, overflow: 'auto', px: 2.5 }}>
      <UsersField disabled={disableUserField || withdrawing} />
      <HoursToggle
        disabled={disabled || withdrawing}
        hours={hours}
        onChange={onHoursToggle}
      />
      <DatesRangeField
        disabled={disabled || (withdrawing && hours)}
        hours={hours}
        minDate={withdrawScope?.minDate}
        maxDate={withdrawScope?.maxDate}
      />
      {hours && (
        <TimeRangeField
          disabled={disabled}
          minTime={withdrawScope?.minTime ?? undefined}
          maxTime={withdrawScope?.maxTime ?? undefined}
        />
      )}
      <TotalDatesOrHours
        dateFrom={dateFrom}
        dateTo={dateTo}
        startTime={startTime}
        endTime={endTime}
        hours={hours}
        isHolidayDate={(date) =>
          holidayLookup.isHolidayDate(date, selectedUserBranchId)
        }
      />
      <TypeField disabled={disabled || withdrawing} />
      <NotesField disabled={disabled} />
      {isHrOfTarget && <NotesHrField disabled={disabled} />}
    </Box>
  );
}
